/**
 * SelfStartOverlay
 * Fullscreen overlay shown on the active member's device when it is their
 * turn to pitch AND selfStartEnabled === true in meetingSettings.
 *
 * Rule 1: The host-start flow in NetworkClubMeeting is untouched.
 * Rule 6: Separate component — max 300 lines.
 * Rule 7: 375px mobile-first design, 44x44px touch targets.
 * Rule 10: Loading state during Firestore write.
 */

import React, { useState } from 'react';
import { db } from '../../../firebase';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { COLLECTIONS, STRINGS, TIMER_DEFAULT_SECONDS } from '../meetingConstants';

interface SelfStartOverlayProps {
  entityId: string;
  roomName: string;
  userId: string;
  presenterName: string;
  timerDurationSeconds: number;
  /** Accent color from club theme */
  accentColor?: string;
  /** Called after the Firestore write succeeds, so parent can react */
  onStarted: () => void;
  /** Called when the user explicitly dismisses without starting */
  onDismiss: () => void;
}

const SelfStartOverlay: React.FC<SelfStartOverlayProps> = ({
  entityId,
  roomName,
  userId,
  presenterName,
  timerDurationSeconds,
  accentColor = '#f97316',
  onStarted,
  onDismiss,
}) => {
  const [isStarting, setIsStarting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const handleStart = async (): Promise<void> => {
    if (isStarting) return;
    setIsStarting(true);
    setErrorMsg(null);

    try {
      const docId = `${entityId}_${roomName}`;
      // Atomic single write — sets runningUserId so the timer starts for everyone
      await setDoc(
        doc(db, COLLECTIONS.LIVE_PITCHES, docId),
        {
          entityId,
          roomName,
          runningUserId: userId,
          startedAtMs: Date.now(),
          timerDurationSeconds: timerDurationSeconds ?? TIMER_DEFAULT_SECONDS,
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
      onStarted();
    } catch (err) {
      console.error('[SelfStartOverlay] Firestore write failed:', err);
      setErrorMsg(STRINGS.errors.generic);
      setIsStarting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[90] flex flex-col items-center justify-center px-6 text-white text-center"
      style={{ backgroundColor: accentColor }}
      role="dialog"
      aria-modal="true"
      aria-label={STRINGS.selfStart.heading}
    >
      {/* Dismiss — accessible close */}
      <button
        onClick={onDismiss}
        aria-label="Sluiten zonder te starten"
        className="absolute top-4 right-4 w-11 h-11 flex items-center justify-center rounded-full bg-black/20 hover:bg-black/35 transition-colors text-lg font-bold"
      >
        ✕
      </button>

      {/* Pulse ring behind heading */}
      <div className="relative mb-6">
        <span className="absolute inset-0 rounded-full bg-white/20 animate-ping" />
        <div className="relative w-20 h-20 rounded-full bg-white/25 flex items-center justify-center text-4xl">
          🎤
        </div>
      </div>

      <h1 className="text-3xl sm:text-4xl font-black leading-tight mb-3">
        {STRINGS.selfStart.heading}
      </h1>
      <p className="text-lg font-semibold opacity-90 mb-2">{presenterName}</p>
      <p className="text-sm opacity-75 mb-8 max-w-xs">
        {STRINGS.selfStart.subheading}
      </p>

      {errorMsg && (
        <p
          role="alert"
          className="mb-4 px-4 py-2 rounded-xl bg-black/25 text-sm font-medium"
        >
          {errorMsg}
        </p>
      )}

      {/* CTA — min 44x44 touch target */}
      <button
        onClick={() => void handleStart()}
        disabled={isStarting}
        aria-busy={isStarting}
        className="min-h-[56px] px-10 rounded-2xl text-lg font-black bg-white transition-all disabled:opacity-60 disabled:cursor-not-allowed active:scale-95"
        style={{ color: accentColor }}
      >
        {isStarting ? STRINGS.selfStart.loading : STRINGS.selfStart.cta}
      </button>

      <p className="mt-5 text-xs opacity-60">
        Timer: {timerDurationSeconds ?? TIMER_DEFAULT_SECONDS} seconden
      </p>
    </div>
  );
};

export default SelfStartOverlay;
